import { z } from "zod/v4";
import { ApiError } from "../../admin-api/internal";

/** How many times a failed read is tried again: the query client's default. */
const MAX_RETRIES = 3;
/** Client errors that are worth another try: the request was too slow or too many. */
const RETRYABLE_CLIENT_STATUSES: readonly number[] = [408, 429];
const FIRST_CLIENT_ERROR = 400;
const LAST_CLIENT_ERROR = 499;

/** The `code` the camp API puts beside `error` when the caller's own account row is not created yet. */
const USER_NOT_SYNCED = "user_not_synced";
const NOT_FOUND = 404;
const errorCodeSchema = z.object({ code: z.string() });

/** A brand-new signup asks the camp API while the Go service is still creating their account row. */
function isAccountStillBeingCreated(error: ApiError): boolean {
	if (error.status !== NOT_FOUND) return false;
	try {
		return errorCodeSchema.safeParse(JSON.parse(error.message)).data?.code === USER_NOT_SYNCED;
	} catch {
		return false;
	}
}

/**
 * A 4xx will answer the same again, so a missing or forbidden camp shows "not found" at once
 * instead of after the backoff; the network and 5xx keep the default retries. The one 404 that
 * does not stay a 404 is `user_not_synced`: the first sign-in races the account's creation.
 */
export function retryServerErrors(failureCount: number, error: unknown): boolean {
	if (failureCount >= MAX_RETRIES) return false;
	if (!(error instanceof ApiError)) return true;
	if (isAccountStillBeingCreated(error)) return true;
	const isClientError = error.status >= FIRST_CLIENT_ERROR && error.status <= LAST_CLIENT_ERROR;
	return !isClientError || RETRYABLE_CLIENT_STATUSES.includes(error.status);
}

/**
 * Keeps the previous result on screen while a new key loads, but only for the same organizer
 * (and the same camp, edition or roster when `recordId` is given): another one's data must never
 * stand in. Key layout: `["camp-portal", organizerId, scope, recordId, ...]`.
 */
export function keepWithin(organizerId: string | null, recordId?: string | null) {
	return <T>(
		previous: T | undefined,
		previousQuery: { queryKey: readonly unknown[] } | undefined,
	): T | undefined => {
		if (previousQuery?.queryKey[1] !== (organizerId ?? "")) return undefined;
		if (recordId !== undefined && previousQuery.queryKey[3] !== (recordId ?? "")) return undefined;
		return previous;
	};
}
